import { useRef, useState } from "react";
import React from "react";
import { Button, Table, Typography, Divider } from "antd";
import RollbackOutlined from "@ant-design/icons/RollbackOutlined";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import ModalBase from "../lib/common/ModalBase";
import { useModal } from "../lib/modal/ModalContext";

interface DataCompareModalProps {
  before: any[] | null;
  after: any[] | null;
  history?: any[] | null; 
  onApplyData: () => void | Promise<void>;
}

// compareRows가 있으면 같은 인덱스 행과 값을 비교해 달라진 셀에 highlightColor를 칠함
const getTableColumns = (
  rows: any[] | null | undefined,
  compareRows?: any[] | null,
  highlightColor?: string
) => {
  if (!Array.isArray(rows) || rows.length === 0 || rows[0] == null) {
    return [];
  }
  return Object.keys(rows[0])
    .filter((k) => k !== "key")
    .map((key) => ({
      title: key,
      dataIndex: key,
      key,
      render: (text: any, record: any) => {
        const other = compareRows?.[record.key];
        const changed =
          other != null &&
          JSON.stringify(text ?? null) !== JSON.stringify(other[key] ?? null);
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 180,
              cursor: "pointer",
              background: changed ? highlightColor : undefined,
              fontWeight: changed ? "bold" : undefined,
            }}
            title={text}
          >
            {typeof text === "string" || typeof text === "number" || typeof text === "boolean" ? String(text) : ""}
          </div>
        );
      },
    }));
};

const getTableData = (rows: any[] | null | undefined) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, idx) => ({
    key: idx,
    ...row,
  }));
};

const DataCompareModal: React.FC<DataCompareModalProps> = ({
  before,
  after,
  history = null,
  onApplyData,
}) => {
  const { isVisible, hideModal, modalContent, showModal } = useModal();
  const [applying, setApplying] = useState(false);

  const beforeRef = useRef<HTMLDivElement>(null);
  const afterRef = useRef<HTMLDivElement>(null);

  const syncScroll = (source: "before" | "after") => {
    const beforeEl = beforeRef.current;
    const afterEl = afterRef.current;

    if (!beforeEl || !afterEl) return;

    if (source === "before") {
      afterEl.scrollTop = beforeEl.scrollTop;
      afterEl.scrollLeft = beforeEl.scrollLeft;
    } else {
      beforeEl.scrollTop = afterEl.scrollTop;
      beforeEl.scrollLeft = afterEl.scrollLeft;
    }
  };

  return (
    <ModalBase>
      <div
        className="w-[90vw] h-[90vh] relative flex flex-col bg-white rounded-lg box-border overflow-auto"
      >
        <div className="p-10">
          <div className="flex flex-col justify-between gap-10">
            <Typography.Title level={1} className="text-4xl">
              데이터 변경을 최종 확인해 주세요
            </Typography.Title>
            <div className="flex flex-col justify-start gap-5">
              {Array.isArray(before) && before.length > 0 && (
                <div
                  className="overflow-scroll"
                  ref={beforeRef}
                  onScroll={() => syncScroll("before")}
                  style={{ maxHeight: 300 }}
                >
                  <Typography.Title level={5}>데이터 변경전</Typography.Title>
                  <Table
                    columns={getTableColumns(before, after, "#ffccc7")}
                    dataSource={getTableData(before)}
                    bordered
                    pagination={false}
                    size="small"
                  />
                </div>
              )}
              {Array.isArray(before) && before.length > 0 && Array.isArray(after) && after.length > 0 && (
                <Divider style={{ margin: "24px 0" }}>
                  <span style={{ fontWeight: "bold", color: "#888" }}>
                    ↓ 변경 후 데이터 ↓
                  </span>
                </Divider>
              )}
              {Array.isArray(after) && after.length > 0 && (
                <div
                  className="overflow-scroll"
                  ref={afterRef}
                  onScroll={() => syncScroll("after")}
                  style={{ maxHeight: 300 }}
                >
                  <Typography.Title level={5}>데이터 변경후</Typography.Title>
                  <Table
                    columns={getTableColumns(after, before, "#d9f7be")}
                    dataSource={getTableData(after)}
                    bordered
                    pagination={false}
                    size="small"
                  />
                </div>
              )}
              {Array.isArray(history) && history.length > 0 && (
                <div
                  className="overflow-scroll mt-4"
                  style={{ maxHeight: 300 }}
                >
                  <Typography.Title level={5}>History</Typography.Title>
                  <Table
                    columns={getTableColumns(history)}
                    dataSource={getTableData(history)}
                    bordered
                    pagination={false}
                    size="small"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="w-full flex justify-center gap-2">
          <Button
            type="primary"
            danger
            style={{ fontWeight: "bold" }}
            icon={<RollbackOutlined />}
            disabled={applying}
            onClick={()=>{
              hideModal();
              alert("작업이 취소 되었습니다");
              window.location.reload();
            }}
          >
            롤백
          </Button>
          <Button
            icon={<CheckOutlined />}
            loading={applying}
            onClick={async () => {
              setApplying(true);
              try {
                await onApplyData();
                alert("적용이 완료되었습니다.");
                hideModal();
                window.location.reload();
              } catch (e) {
                alert("적용 중 오류가 발생했습니다.\n데이터가 실제로 반영됐는지 확인 후 다시 시도해주세요.");
                setApplying(false);
              }
            }}
          >
            적용하기
          </Button>
        </div>
      </div>
    </ModalBase>
  );
};

export default DataCompareModal;
